import { useCallback, useState } from 'react';
import LangEnum from '../../lang/LangEnum';
import { GeneralSettingsParams } from '../../stores/GeneralSettings/generalSettings.types';
import useGeneralSettingsStore from '../../stores/GeneralSettings/useGeneralSettingsStore';
import snackbarStore from '../../stores/Snackbar.store';
import { BrandSplitSettings } from '../brand-split-settings/brandSplitSettings.types';
import useBrandSplitSettings from '../brand-split-settings/useBrandSplitSettings';
import { brandGeneralSettingsRepository } from '../brand/brand.repository';
import { ReportPeriodFilterEnum } from '../reports/reports-page/Enum/ReportPeriodFilterEnum';
import { useTranslation } from '../translation/TranslationContext';
import { HandleChangeParams, handleChangeAndBlurParams } from './types/generalSettingsPage.types';
import { deleteContract, getBrandContract, uploadContractLayout } from './uploadContract';

const useGeneralSettings = () => {
  const {
    fetchSplitSettings,
    updateSplitSettings,
    splitSettings,
    selectedSplitSettings,
    orderSeparation,
  } = useBrandSplitSettings();
  const { updateGeneralSettings, fetchGeneralSettings, generalSettings } =
    useGeneralSettingsStore();
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [contract, setContract] = useState(null);
  const [link, setLink] = useState(null);

  const fetchContract = useCallback(async () => {
    const response = await getBrandContract();
    if (response && response.data) {
      const url = response.data;
      const splitUrl = url.split('net/')[1].split('-');
      const joinUrl = splitUrl.splice(5, splitUrl.length).join('');
      setLink(url);
      setContract(joinUrl);
    }
  }, []);

  const getSettingsPage = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchContract(), fetchGeneralSettings(), fetchSplitSettings()]);
    setIsLoading(false);
  }, [fetchContract, fetchGeneralSettings, fetchSplitSettings]);

  const handleBlur = async (values?: GeneralSettingsParams) => {
    const updateDTO = values || generalSettings;

    const dto = {
      ...updateDTO,
      orderCollectionId:
        orderSeparation || !updateDTO.orderCollection?.id ? null : updateDTO.orderCollection?.id,
      preOrderCollectionId:
        !orderSeparation || !updateDTO.preOrderCollection?.id
          ? null
          : updateDTO.preOrderCollection?.id,
      readyForDeliveryCollectionId:
        !orderSeparation || !updateDTO.readyForDeliveryCollection?.id
          ? null
          : updateDTO.readyForDeliveryCollection?.id,
      suggestedClassificationTypeId: updateDTO.suggestedClassificationType?.id ?? null,
      autoAcceptInviteFromCustomerWithRepresentative:
        updateDTO.autoAcceptInviteFromCustomerWithRepresentative ?? false,
      allowRepresentativesToInviteCustomers:
        updateDTO.allowRepresentativesToInviteCustomers ?? false,
    };

    const response = await brandGeneralSettingsRepository().update(dto, {});
    if (response.ok) {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.GLOBAL_SUCCESS_MESSAGE),
      });
      updateGeneralSettings({ ...updateDTO, ...response.data });
      return;
    }

    handleMessage({
      type: 'error',
      message: response.data?.code
        ? translate(response.data.code as LangEnum)
        : translate(LangEnum.GLOBAL_ERROR_MESSAGE),
    });
  };

  const handleDeleteFile = async () => {
    const delContract = await deleteContract();
    const verifyContract = await getBrandContract();

    setContract(verifyContract.data);

    if (!delContract) {
      handleMessage({
        type: 'error',
        message: translate(LangEnum.GENERAL_SETTINGS_DELETE_CONTRACT_ERROR),
      });
    }
  };

  const handleChangeFile = async (files: File[]) => {
    setIsLoadingContract(true);

    const response = await uploadContractLayout(files);

    if (response.ok) {
      const verifyContract = await getBrandContract();
      if (verifyContract && verifyContract.data) {
        const url = verifyContract.data;
        const splitUrl = url.split('net/')[1].split('-');
        const joinUrl = splitUrl.splice(5, splitUrl.length).join('');
        setLink(url);
        setContract(joinUrl);
      }
    } else {
      handleMessage({
        type: 'error',
        message: response?.data?.message ?? translate(LangEnum.PRODUCT_COLORS_FORM_IMAGE_ERROR),
      });
    }
    setIsLoadingContract(false);
  };

  const handleChangeSplitSettings = async (keys: string[]) => {
    const newSelectedSplitSettings = keys.reduce((prev, key) => {
      const found = splitSettings.find((split) => split.key === key);

      return found ? [...prev, found] : prev;
    }, [] as BrandSplitSettings[]);

    await updateSplitSettings(newSelectedSplitSettings);
  };

  const handleChange = async ({ value, name }: HandleChangeParams) => {
    updateGeneralSettings({ ...generalSettings, [name]: value });
  };

  const handleChangeAndBlur = async ({ value, name }: handleChangeAndBlurParams) => {
    if (
      name === 'reportPeriodFilter' &&
      (value === ReportPeriodFilterEnum.ORDER_COLLECTION ||
        value === ReportPeriodFilterEnum.PRODUCT_COLLECTION)
    ) {
      const { orderCollection, preOrderCollection, readyForDeliveryCollection } = generalSettings;
      if (!orderCollection && !preOrderCollection && !readyForDeliveryCollection) {
        handleMessage({
          type: 'error',
          message: translate(LangEnum.REPORT_PERIOD_FILTER_ERROR),
        });
        updateGeneralSettings({
          ...generalSettings,
          [name]: ReportPeriodFilterEnum.ORDER_CREATE_DATE,
        });
        return;
      }
    }
    handleBlur({ ...generalSettings, [name]: value });
  };

  const handleChangeAndBlurList = async (values: handleChangeAndBlurParams[]) => {
    const updates = values.reduce(
      (acc, { value, name }) => {
        return { ...acc, [name]: value };
      },
      {} as Partial<GeneralSettingsParams>,
    );

    const updatedSettings = { ...generalSettings, ...updates };
    updateGeneralSettings(updatedSettings);
    handleBlur(updatedSettings);
  };

  return {
    splitSettings,
    selectedSplitSettings,
    orderSeparation,
    generalSettings,
    isLoading,
    isLoadingContract,
    contract,
    link,
    fetchContract,
    fetchGeneralSettings,
    getSettingsPage,
    handleBlur,
    handleDeleteFile,
    handleChangeFile,
    handleChangeSplitSettings,
    handleChange,
    handleChangeAndBlur,
    handleChangeAndBlurList,
  };
};

export default useGeneralSettings;
